import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PageLoader from '../ui/PageLoader'
import { hubOpenPath, type HubProject } from '../../api/data360'
import { formatData360DisplayText } from '../../utils/data360DisplayName'
import { useProjects } from '../../contexts/ProjectsContext'
import { useProjectHubViewMode } from '../../hooks/useProjectHubViewMode'
import { filterHubProjects } from '../../utils/hubProjectSearch'
import ProjectHubCard from './projectHub/ProjectHubCard'
import ProjectHubListView from './projectHub/ProjectHubListView'

export default function ProjectHub() {
  const { hubProjects, hubWarnings, loading, hubSearchQuery } = useProjects()
  const [viewMode] = useProjectHubViewMode()
  const navigate = useNavigate()
  const [dismissedWarnings, setDismissedWarnings] = useState(false)

  const handleOpen = useCallback(
    (project: HubProject) => {
      try {
        navigate(hubOpenPath(project))
      } catch (err: unknown) {
        console.error('Failed to open project:', err)
      }
    },
    [navigate]
  )

  const allProjects = hubProjects ?? []
  const displayProjects = useMemo(
    () => filterHubProjects(allProjects, hubSearchQuery),
    [allProjects, hubSearchQuery]
  )
  const hasSearchQuery = hubSearchQuery.trim().length > 0
  const activeWarnings = dismissedWarnings ? [] : hubWarnings

  if (loading) {
    return <PageLoader label="Loading projects…" compact data-testid="project-hub-loader" />
  }

  return (
    <div className="space-y-4">
      {activeWarnings.length > 0 ? (
        <div
          className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="project-hub-data360-warning"
          role="alert"
        >
          <div className="min-w-0 space-y-1">
            {activeWarnings.map((warning) => (
              <p key={warning} className="text-xs text-amber-900">
                {formatData360DisplayText(warning)}
              </p>
            ))}
            <p className="text-xs text-amber-800">
              Existing legacy projects are still listed below.
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-amber-900 underline"
            onClick={() => setDismissedWarnings(true)}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {allProjects.length === 0 ? (
        <div className="rounded-xl border border-[#E2E5EA] bg-white p-5">
          <p className="text-xs text-gray-500">No projects yet — create a project to get started.</p>
        </div>
      ) : displayProjects.length === 0 && hasSearchQuery ? (
        <div
          className="rounded-xl border border-[#E2E5EA] bg-white p-5"
          data-testid="project-hub-no-search-results"
        >
          <p className="text-xs text-gray-500">
            No projects match your search for &ldquo;{hubSearchQuery.trim()}&rdquo;.
          </p>
        </div>
      ) : (
        <div
          key={viewMode}
          className="motion-reduce:transition-none animate-[fadeIn_0.15s_ease-out]"
        >
          {viewMode === 'list' ? (
            <ProjectHubListView
              projects={displayProjects}
              onOpen={handleOpen}
            />
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {displayProjects.map((project) => (
                <ProjectHubCard
                  key={project.wpsShadowId || project.id || `d360-${project.data360ProjectId}`}
                  project={project}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
